import { useEffect, useState } from 'react';
import { siteMeta } from '@/mocks/cinema';

const navLinks = [
  { id: 'top', label: '首页' },
  { id: 'boxoffice', label: '实时票房' },
  { id: 'schedule', label: '今日排片' },
  { id: 'release', label: '本周上映' },
  { id: 'window', label: '档期窗口' },
  { id: 'news', label: '票房解读' },
  { id: 'faq', label: '常见问题' },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState('top');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const sections = navLinks.map((l) => l.id).filter((id) => id !== 'top');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: '-45% 0px -50% 0px' },
    );
    sections.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const goto = (id: string) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/90 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 md:px-6">
        <a
          href="#top"
          className="flex items-center gap-2 whitespace-nowrap"
          onClick={(e) => {
            e.preventDefault();
            goto('top');
          }}
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-600 text-background-50">
            <i className="ri-movie-2-fill text-lg"></i>
          </span>
          <span className={`font-heading text-lg font-bold tracking-wide ${scrolled ? 'text-foreground-900' : 'text-foreground-900'} `}>
            {siteMeta.name}
          </span>
        </a>

        <ul className="hidden items-center gap-1 lg:flex">
          {navLinks.map((l) => (
            <li key={l.id}>
              <a
                href={`#${l.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  goto(l.id);
                }}
                className={`rounded-full px-3 py-2 text-sm font-medium transition-colors ${
                  active === l.id
                    ? 'bg-primary-100 text-primary-700'
                    : scrolled
                      ? 'text-foreground-700 hover:bg-background-100'
                      : 'text-foreground-800 hover:bg-background-50/40'
                }`}
              >
                {l.label}
              </a>
            </li>
          ))}
        </ul>

        <a
          href="#release"
          onClick={(e) => {
            e.preventDefault();
            goto('release');
          }}
          className="hidden whitespace-nowrap rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700 lg:inline-flex"
        >
          本周新片
          <i className="ri-arrow-right-up-line ml-1 text-sm"></i>
        </a>

        <button
          type="button"
          aria-label="打开菜单"
          onClick={() => setOpen((v) => !v)}
          className="flex h-10 w-10 items-center justify-center rounded-md border border-background-300 text-foreground-800 lg:hidden"
        >
          <i className={open ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'}></i>
        </button>
      </nav>

      {open && (
        <div className="border-t border-background-200 bg-background-50 px-4 pb-4 pt-2 lg:hidden">
          <ul className="space-y-1">
            {navLinks.map((l) => (
              <li key={l.id}>
                <a
                  href={`#${l.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    goto(l.id);
                  }}
                  className={`block rounded-md px-3 py-2.5 text-sm font-medium ${
                    active === l.id
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-foreground-700 hover:bg-background-100'
                  }`}
                >
                  {l.label}
                </a>
              </li>
            ))}
          </ul>
          <a
            href="#release"
            onClick={(e) => {
              e.preventDefault();
              goto('release');
            }}
            className="mt-2 block whitespace-nowrap rounded-md bg-primary-600 px-4 py-2.5 text-center text-sm font-medium text-background-50 hover:bg-primary-700"
          >
            查看本周新片
          </a>
        </div>
      )}
    </header>
  );
}